import { Card, Descriptions, Table, Tag } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CURRENCY_SYMBOL,
  PRICER_TYPES,
  PRICING_MODES,
  PRICING_UNITS,
  STATUS_PUBLICATION,
  TAG_COLORS,
} from "utils/constants";
import PricingInput from "../Components/PricingInput";
import PricingSummary from "../Components/PricingSummary";
import { planPricingMode } from "../pricingMode";
import { activeExampleCount } from "../reimbursementExamples";

const ReviewSummary = ({ values, setFieldValue, data }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const pricingMode = planPricingMode(
    get(values, "config"),
    get(values, "pricer.type"),
  );
  const coverages = get(values, "insurancePlanCoverages", []);

  return (
    <div className="configure-step">
      <Card title={t("CONFIGURE.REVIEW.SUMMARY_TITLE")} className="m-bottom-16">
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label={t("CONFIGURE.REVIEW.NAME")}>
            {get(values, "name")}
          </Descriptions.Item>
          <Descriptions.Item label={t("CONFIGURE.REVIEW.IDENTIFIER")}>
            {get(values, "identifier")}
          </Descriptions.Item>
          <Descriptions.Item label={t("CONFIGURE.REVIEW.RISK")}>
            <Tag>{get(values, "risk")}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t("CONFIGURE.REVIEW.STATUS")}>
            <Tag
              color={
                get(values, "status") === STATUS_PUBLICATION.ACTIVE
                  ? TAG_COLORS.GREEN
                  : TAG_COLORS.DEFAULT
              }
            >
              {get(values, "status")}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t("CONFIGURE.REVIEW.PRICING_MODE")}>
            <Tag
              color={
                pricingMode === PRICING_MODES.COMPOSABLE
                  ? TAG_COLORS.GREEN
                  : TAG_COLORS.BLUE
              }
            >
              {pricingMode === PRICING_MODES.COMPOSABLE
                ? t("CONFIGURE.TAGS.COMPOSABLE")
                : t("CONFIGURE.TAGS.PACKAGED")}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t("CONFIGURE.REVIEW.COVERAGES")}>
            <Tag color={TAG_COLORS.BLUE}>{coverages.length}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t("CONFIGURE.REVIEW.EXAMPLES")}>
            <Tag color={TAG_COLORS.BLUE}>
              {activeExampleCount(get(values, "supportingInformation"))}
            </Tag>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {pricingMode === PRICING_MODES.PACKAGED && (
        <Card title={t("CONFIGURE.REVIEW.PLAN_PRICE")} className="m-bottom-16">
          <p className="configure-step__muted m-bottom-16">
            {t("CONFIGURE.REVIEW.PLAN_PRICE_INTRO")}
          </p>
          <PricingInput
            value={get(data, "pricer.prices")}
            onChange={() => {}}
            label={t("CONFIGURE.REVIEW.MONTHLY_PLAN_PRICE")}
          />
          <p className="configure-step__hint mt-8">
            {t("CONFIGURE.REVIEW.PRICER_NOTE")}
          </p>
        </Card>
      )}

      {pricingMode === PRICING_MODES.COMPOSABLE && (
        <PricingSummary coverages={coverages} />
      )}

      <Card
        title={t("CONFIGURE.REVIEW.COVERAGES_DETAIL")}
        className="m-bottom-16"
      >
        {coverages.map((coverage) => {
          const category = get(coverage, "coverageCategory", {});
          const mappings = get(coverage, "coverageMappings", []);
          const covMode =
            get(coverage, "pricer.type") === PRICER_TYPES.COMPOSABLE
              ? PRICING_MODES.COMPOSABLE
              : PRICING_MODES.PACKAGED;

          return (
            <Card
              key={coverage.id}
              size="small"
              className="m-bottom-8"
              title={
                <div className="d-flex align--center gap-8">
                  {category.icon && (
                    <img
                      src={category.icon}
                      alt=""
                      className="configure-table-icon"
                    />
                  )}
                  {category.label || category.value}
                  <Tag>
                    {covMode === PRICING_MODES.COMPOSABLE
                      ? t("CONFIGURE.TAGS.COMPOSABLE")
                      : t("CONFIGURE.TAGS.PACKAGED")}
                  </Tag>
                </div>
              }
            >
              <Table
                dataSource={mappings}
                rowKey="id"
                size="small"
                pagination={false}
                columns={[
                  {
                    title: t("CONFIGURE.REVIEW.BENEFIT"),
                    dataIndex: ["coverage", "type"],
                    render: (type, record) => (
                      <span>
                        {type}
                        {get(record, "coverage.subtype") &&
                          ` - ${get(record, "coverage.subtype")}`}
                      </span>
                    ),
                  },
                  {
                    title: t("CONFIGURE.REVIEW.WAITING_PERIOD"),
                    dataIndex: "carenceDays",
                    width: 80,
                    render: (days) =>
                      days
                        ? `${days} ${t("CONFIGURE.REVIEW.DAYS_SUFFIX")}`
                        : "-",
                  },
                  {
                    title: t("CONFIGURE.REVIEW.LIMITS"),
                    dataIndex: "limits",
                    width: 80,
                    render: (limits) => (
                      <span>
                        {(limits || []).length > 0
                          ? t("CONFIGURE.REVIEW.LIMITS_COUNT", {
                              count: limits.length,
                            })
                          : "-"}
                      </span>
                    ),
                  },
                  {
                    title: t("CONFIGURE.REVIEW.PRICE"),
                    width: 160,
                    render: (_, record) => {
                      const pricer =
                        covMode === PRICING_MODES.PACKAGED
                          ? get(coverage, "pricer")
                          : get(record, "pricer");
                      const prices = get(pricer, "prices");
                      if (!prices) return <Tag>-</Tag>;

                      const pType = get(prices, "type", "flat");
                      const unit = get(prices, "unit", PRICING_UNITS.EUR);
                      const suffix =
                        unit === PRICING_UNITS.PMSS
                          ? CURRENCY_SYMBOL.PMSS
                          : "€";
                      const perMonth = t("CONFIGURE.PRICING_SUMMARY.PER_MONTH");

                      if (pType === "flat") {
                        const monthly = get(prices, "monthly");
                        if (!monthly) return <Tag>-</Tag>;
                        return covMode === PRICING_MODES.PACKAGED ? (
                          <Tag color={TAG_COLORS.BLUE}>
                            {monthly} {suffix}
                            {perMonth} {t("CONFIGURE.REVIEW.PACKAGED_SUFFIX")}
                          </Tag>
                        ) : (
                          <Tag color={TAG_COLORS.GREEN}>
                            {monthly} {suffix}
                            {perMonth}
                          </Tag>
                        );
                      }

                      const base = get(prices, "base", {});
                      const priceValues = Object.values(base).filter(
                        (v) => v && v > 0,
                      );
                      if (priceValues.length === 0) return <Tag>-</Tag>;

                      const min = Math.min(...priceValues);
                      const max = Math.max(...priceValues);
                      const range = min === max ? `${min}` : `${min} - ${max}`;
                      return covMode === PRICING_MODES.PACKAGED ? (
                        <Tag color={TAG_COLORS.BLUE}>
                          {range} {suffix}
                          {perMonth} {t("CONFIGURE.REVIEW.PACKAGED_SUFFIX")}
                        </Tag>
                      ) : (
                        <Tag color={TAG_COLORS.GREEN}>
                          {range} {suffix}
                          {perMonth}
                        </Tag>
                      );
                    },
                  },
                ]}
              />
            </Card>
          );
        })}
      </Card>
    </div>
  );
};

export default ReviewSummary;
